import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import {
  formatCompanionDate,
  formatCompanionMoney,
} from '@/components/companion/feed/companion-formatters';
import { Stack } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionPayoutPendingEvidence } from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The pending payout card, as the companion panel shows it.
 *
 * Every amount stays under the currency it was earned in, on its own line. A
 * card about money must never show a figure the brand cannot check, and a
 * single number spanning two currencies is exactly that.
 *
 * What it offers is preparing a payout, never sending one: the beneficiaries
 * are handed to the brand's own checkout, which recomputes what each is owed
 * and keeps the last word on who may receive it. Beneficiaries the platform
 * already knows cannot be paid are named apart and counted into nothing.
 */

interface PayoutPendingCardProps {
  action: CompanionActionDto & { evidence: CompanionPayoutPendingEvidence };
  /** Opens the payouts page on these beneficiaries. Wired by the panel. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

export const PayoutPendingCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: PayoutPendingCardProps) => {
  const { t, i18n } = useTranslation();
  const { evidence } = action;
  const language = i18n.language;

  return (
    <CompanionCardShell
      testId="companion-payout-pending-card"
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      {/* No eyebrow: how long the money has waited is an age, not a deadline,
          and the date of the oldest commission is already an evidence line. */}
      <CompanionCardHeader title={action.title} reason={action.reason} />

      <Stack gap="2px">
        {/* One line per currency, in the order the server grouped them. Adding
            them would produce a number that means nothing to anyone. */}
        {evidence.amountsByCurrency.map((entry) => (
          <EvidenceLine key={entry.currencyCode}>
            {t('companion.cards.payoutPending.amount', {
              amount: formatCompanionMoney(
                { amountCents: entry.totalAmountCents, currencyCode: entry.currencyCode },
                language
              ),
              count: entry.commissionCount,
            })}
          </EvidenceLine>
        ))}
        <EvidenceLine>
          {t('companion.cards.payoutPending.affiliates', { count: evidence.affiliateCount })}
        </EvidenceLine>
        <EvidenceLine>
          {t('companion.cards.payoutPending.oldest', {
            date: formatCompanionDate(evidence.oldestPendingAt, language),
          })}
        </EvidenceLine>
        {evidence.excludedAffiliates.length > 0 ? (
          <EvidenceLine>
            {t('companion.cards.payoutPending.excluded', {
              count: evidence.excludedAffiliates.length,
            })}
          </EvidenceLine>
        ) : null}
        {evidence.brandPayoutConfigured ? null : (
          <EvidenceLine>{t('companion.cards.payoutPending.billingMissing')}</EvidenceLine>
        )}
      </Stack>
    </CompanionCardShell>
  );
};
